import * as React from 'react';
import { Helmet } from 'react-helmet';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';

import {
  PageConfig,
  Customizations,
  HasPermissionFn,
  GraphQLFilter,
} from '../../../@types';
import DataViewContainer from '../../../containers/DataViewContainer';
import CustomViewContainer from '../../../containers/CustomViewContainer';
import FilterViewContainer from '../../../containers/FilterViewContainer';
import TextViewContainer from '../../../containers/TextViewContainer';
import LayoutViewContainer from '../../../containers/LayoutViewContainer';

import styles from './DynamicSection.module.scss';
import SwitchTabViewContainer from '../../../containers/SwitchTabViewContainer';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  title: string;
  data: PageConfig;
  customizations?: Customizations;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
  hasPermission?: HasPermissionFn;
}

/**
 * <DynamicSection> page
 */
const DynamicSection: React.FunctionComponent<Props> = ({
  className,
  title,
  data,
  customizations,
  baseFilter,
  permissionFilter,
  hasPermission,
  ...htmlElementProps
}) => (
  <div className={classNames(styles.section, className)} {...htmlElementProps}>
    <Helmet>
      <title>{title}</title>
    </Helmet>

    {data.items.flatMap((d) => {
      if (hasPermission && d.permission && !hasPermission(d.permission)) {
        return null;
      }

      // Page filters
      if (d.type === 'filter-view') {
        return (
          <FilterViewContainer
            key={uuid()}
            className="mb-4"
            data={d}
            baseFilter={baseFilter}
            permissionFilter={permissionFilter}
            hasPermission={hasPermission}
          />
        );
      }

      // Data views
      if (d.type === 'data-view') {
        return (
          <DataViewContainer
            key={uuid()}
            data={d}
            customizations={customizations}
            baseFilter={baseFilter}
            permissionFilter={permissionFilter}
            hasPermission={hasPermission}
          />
        );
      }

      // Text views
      if (d.type === 'text-view') {
        return <TextViewContainer key={uuid()} data={d} />;
      }

      // Layout views
      if (d.type === 'layout-view') {
        return (
          <LayoutViewContainer
            key={uuid()}
            data={d}
            customizations={customizations}
            baseFilter={baseFilter}
            permissionFilter={permissionFilter}
            hasPermission={hasPermission}
          />
        );
      }

      // Custom views
      if (d.type === 'custom-view') {
        return (
          <CustomViewContainer
            key={uuid()}
            data={d}
            baseFilter={baseFilter}
            permissionFilter={permissionFilter}
            hasPermission={hasPermission}
            customizations={customizations}
          />
        );
      }

      // Switch tab views
      if (d.type === 'switch-tab-view') {
        return (
          <SwitchTabViewContainer
            key={uuid()}
            data={d}
            customizations={customizations}
            baseFilter={baseFilter}
            permissionFilter={permissionFilter}
          />
        );
      }
      return null;
    })}
  </div>
);

export default DynamicSection;
